import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const POINTS = [
  { name: "Panvel", lat: 18.9894, lng: 73.1175 },
  { name: "Mangaon", lat: 18.2350, lng: 73.2858 },
  { name: "Wadkhal", lat: 18.7500, lng: 73.0500 },
  { name: "Ambernath", lat: 19.2167, lng: 73.1833 },
  { name: "Shahpur", lat: 19.4500, lng: 73.3500 },
  { name: "Bhayandar", lat: 19.3022, lng: 72.8514 },
  { name: "Palghar", lat: 19.6967, lng: 72.7656 },
  { name: "Dhule", lat: 20.9042, lng: 74.7749 },
];

/**
 * ServiceMap — interactive Leaflet map of Bhumi's 8 service touch points.
 * Uses OpenStreetMap tiles (no API key). Lazy-imports leaflet client-side.
 */
export default function ServiceMap() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = (await import("leaflet")).default;
      await import("leaflet/dist/leaflet.css");
      if (cancelled || !containerRef.current || mapRef.current) return;

      const map = L.map(containerRef.current, {
        scrollWheelZoom: false,
        zoomControl: true,
        attributionControl: true,
      }).setView([19.4, 73.3], 7);
      mapRef.current = map;

      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
        {
          attribution:
            '© <a href="https://carto.com/attributions">CARTO</a> · © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          subdomains: "abcd",
          maxZoom: 19,
        }
      ).addTo(map);

      const orangeIcon = L.divIcon({
        className: "be-pin",
        html: `<div style="
          width: 28px; height: 28px; border-radius: 50%;
          background: #ff6a13; border: 3px solid #fff;
          box-shadow: 0 0 0 4px rgba(255,106,19,0.25), 0 6px 12px rgba(0,0,0,0.4);
          display:flex; align-items:center; justify-content:center;
          color:#fff; font-weight:900; font-size:11px;
        ">●</div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      });

      const bounds = [];
      POINTS.forEach((p) => {
        const m = L.marker([p.lat, p.lng], { icon: orangeIcon }).addTo(map);
        m.bindPopup(
          `<div style="font-family:Figtree,sans-serif;">
            <div style="font-size:10px; letter-spacing:0.18em; color:#ff6a13; font-weight:700; text-transform:uppercase;">Touch Point</div>
            <div style="font-size:18px; font-weight:800; color:#111; margin-top:4px;">${p.name}</div>
            <div style="font-size:11px; color:#666; margin-top:2px;">Maharashtra · Bhumi Equipments</div>
          </div>`
        );
        bounds.push([p.lat, p.lng]);
      });

      if (bounds.length) map.fitBounds(bounds, { padding: [40, 40] });
    })();

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  const scrollContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="space-y-6" data-testid="service-map-wrap">
      <div
        ref={containerRef}
        className="w-full h-[420px] md:h-[540px] rounded-3xl overflow-hidden border border-white/10"
        data-testid="service-map"
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {POINTS.map((p) => (
          <button
            key={p.name}
            onClick={scrollContact}
            className="group text-left bg-white/5 hover:bg-[#ff6a13] border border-white/10 px-4 py-3 rounded-xl transition-colors"
            data-testid={`map-area-${p.name.toLowerCase()}`}
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 group-hover:text-white/80">
              Touch Point
            </div>
            <div className="font-display text-base font-bold text-white flex items-center gap-1">
              {p.name} <ArrowRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
